import React, { useEffect, useState } from 'react'
import { Head, Link, router, usePage } from '@inertiajs/react'
import { toast } from 'sonner'
import DashboardLayout from '../layouts/DashboardLayout'
import { STATUS_STYLE } from './FinanceReview'
import { ChevronLeft, Download, Eye, FileText, Image as ImageIcon, Lock, ShieldCheck, History, UserRound } from 'lucide-react'

// One finance application in the lender's tab (Paul 2026-09-30). Left: everything the patient gave, plus the
// documents. Right: the lender's decisions. Every view, download and decision is logged server-side.

const card = 'rounded-2xl border border-brand-border bg-white p-5 shadow-sm'
const inputCls = 'w-full rounded-lg border border-brand-border bg-white px-3 py-2 text-sm text-brand-ink focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/20'
const btn = 'inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition disabled:opacity-50'
const when = (iso) => (iso ? new Date(iso).toLocaleString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—')
const kb = (n) => (n >= 1048576 ? (n / 1048576).toFixed(1) + ' MB' : Math.max(1, Math.round(n / 1024)) + ' KB')

function Rows({ rows }) {
  return (
    <dl className="grid grid-cols-[minmax(0,42%)_minmax(0,1fr)] gap-x-4 gap-y-1.5 text-sm">
      {rows.map(([k, v]) => (
        <React.Fragment key={k}><dt className="text-brand-muted">{k}</dt><dd className="break-words text-brand-ink">{v}</dd></React.Fragment>
      ))}
    </dl>
  )
}

function Password({ value }) {
  const [show, setShow] = useState(false)
  if (!value) return <span className="text-red-600">Password not given</span>
  return (
    <span className="inline-flex items-center gap-2">
      <Lock size={12} /> {show ? <code className="rounded bg-brand-surface px-1.5">{value}</code> : '••••••'}
      <button type="button" className="text-xs font-semibold text-brand-primary" onClick={() => setShow((s) => !s)}>{show ? 'Hide' : 'Show password'}</button>
    </span>
  )
}

export default function FinanceReviewShow({ app, decline_categories = {}, is_admin, lender = {} }) {
  const { props } = usePage()
  const flash = props.flash || {}
  useEffect(() => {
    if (flash.notice) toast.success(flash.notice)
    if (flash.alert) toast.error(flash.alert)
  }, [flash.notice, flash.alert])

  const [busy, setBusy] = useState(false)
  const [info, setInfo] = useState('')
  const [appr, setAppr] = useState({ amount: app.suggest?.amount || '', term: app.suggest?.term || '', rate: app.suggest?.rate || '', instalment: app.suggest?.instalment || '' })
  const [category, setCategory] = useState('')
  const [note, setNote] = useState(app.lender_note || '')
  const can = (a) => (app.actions || []).includes(a)

  function act(payload, confirmText) {
    if (confirmText && !window.confirm(confirmText)) return
    setBusy(true)
    router.post(`/finance-review/${app.id}/status`, payload, { preserveScroll: true, onFinish: () => setBusy(false) })
  }

  return (
    <DashboardLayout>
      <Head title={`${app.reference} · Finance application`} />
      <div className="mx-auto max-w-6xl space-y-5">
        <Link href="/finance-review" className="inline-flex items-center gap-1 text-sm font-semibold text-brand-primary"><ChevronLeft size={16} /> All applications</Link>

        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="font-mono text-2xl font-semibold text-brand-ink">{app.reference}</h1>
            <p className="mt-1 text-sm text-brand-muted">Sent {when(app.submitted_at)}{app.opened_at ? ` · opened ${when(app.opened_at)}` : ''}{app.decided_at ? ` · decided ${when(app.decided_at)}` : ''}</p>
          </div>
          <div className="flex items-center gap-2">
            {app.patient_url && <Link href={app.patient_url} className={`${btn} border border-brand-border bg-white text-brand-ink`}><UserRound size={15} /> Patient record</Link>}
            <span className={`inline-flex rounded-full border px-3 py-1 text-sm font-semibold ${STATUS_STYLE[app.status] || ''}`}>{app.status_label}</span>
          </div>
        </div>

        {app.purged_at && (
          <div className="rounded-xl border border-brand-border bg-brand-surface px-4 py-3 text-sm text-brand-muted">
            The documents and answers were deleted on {when(app.purged_at)}, 30 days after the decision, as the POPIA retention rule requires. This record keeps only the dates, amounts and outcome.
          </div>
        )}

        <div className="grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,1fr)_340px]">
          {/* ── The pack ── */}
          <div className="space-y-5">
            <div className={card}>
              <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-brand-muted">Loan request</h2>
              <Rows rows={app.loan || []} />
              <p className="mt-3 text-sm text-brand-ink">{app.treatment}</p>
              <p className="mt-1 text-xs text-brand-muted">{app.payout_rule}</p>
            </div>

            {!app.purged_at && (
              <div className={card}>
                <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                  <h2 className="text-sm font-semibold uppercase tracking-wide text-brand-muted">Documents</h2>
                  {app.documents?.length > 0 && <a href={app.pack_url} className={`${btn} bg-brand-primary text-white hover:opacity-90`}><Download size={15} /> Download everything (zip)</a>}
                </div>
                <div className="space-y-2">
                  {(app.documents || []).map((d) => (
                    <div key={d.id} className="flex flex-wrap items-center gap-3 rounded-xl border border-brand-border p-3">
                      <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-brand-accent text-brand-primary">{d.image ? <ImageIcon size={18} /> : <FileText size={18} />}</span>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold text-brand-ink">{d.label}</div>
                        <div className="break-all text-xs text-brand-muted">{d.name} · {kb(d.size)}{d.pages ? ` · ${d.pages} page${d.pages > 1 ? 's' : ''}` : ''} · added {when(d.uploaded_at)}</div>
                        {d.locked && <div className="mt-1 text-xs text-amber-800"><Password value={d.password} /></div>}
                      </div>
                      <div className="flex gap-2">
                        <a href={d.url} target="_blank" rel="noopener noreferrer" className={`${btn} border border-brand-border bg-white text-brand-ink`}><Eye size={15} /> View</a>
                        <a href={d.download_url} className={`${btn} border border-brand-border bg-white text-brand-ink`}><Download size={15} /> Download</a>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="mt-3 text-xs text-brand-muted">The patient confirmed the statements cover {app.statements_cover}{app.statements_confirmed ? '.' : ' (not confirmed).'}</p>
              </div>
            )}

            {(app.sections || []).map((s) => (
              <div key={s.title} className={card}>
                <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-brand-muted">{s.title}</h2>
                <Rows rows={s.rows} />
              </div>
            ))}

            <div className={card}>
              <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-brand-muted"><ShieldCheck size={15} /> Consent record</h2>
              <Rows rows={(app.consents || []).map(([k, v]) => [k, typeof v === 'string' && v.includes('T') ? when(v) : v])} />
            </div>

            <div className={card}>
              <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-brand-muted"><History size={15} /> Activity</h2>
              <ol className="space-y-1.5 text-sm">
                {(app.events || []).map((e, i) => (
                  <li key={i} className="flex flex-wrap gap-x-3 text-brand-ink"><span className="w-40 flex-shrink-0 text-brand-muted">{when(e.at)}</span><span className="font-medium">{e.label}</span><span className="text-brand-muted">{e.actor}</span></li>
                ))}
              </ol>
            </div>
          </div>

          {/* ── Decisions ── */}
          <div className="space-y-5 lg:sticky lg:top-20 lg:self-start">
            {(app.decision?.amount || app.decision?.category) && (
              <div className={card}>
                <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-brand-muted">Decision</h2>
                {app.decision.category ? <Rows rows={[['Declined', app.decision.category]]} /> : (
                  <Rows rows={[['Approved amount', app.decision.amount], ['Term', `${app.decision.term} months`], ['Rate', app.decision.rate], ['Instalment', app.decision.instalment]]} />
                )}
              </div>
            )}

            {can('request_info') && (
              <div className={card + ' space-y-3'}>
                <h2 className="font-semibold text-brand-ink">Ask the patient for more</h2>
                <textarea className={inputCls} rows={3} value={info} onChange={(e) => setInfo(e.target.value)} placeholder="For example: a payslip for July 2026" />
                <button type="button" disabled={busy || !info.trim()} onClick={() => act({ do: 'request_info', text: info })} className={`${btn} w-full border border-amber-300 bg-amber-50 text-amber-800`}>Request information</button>
                <p className="text-xs text-brand-muted">The patient sees your request on their status page and can upload the document there.</p>
              </div>
            )}

            {can('approve') && (
              <div className={card + ' space-y-3'}>
                <h2 className="font-semibold text-brand-ink">Approve</h2>
                <div className="grid grid-cols-2 gap-2">
                  {[['amount', 'Amount (R)'], ['term', 'Term (months)'], ['rate', 'Rate (% a year)'], ['instalment', 'Instalment (R)']].map(([k, label]) => (
                    <label key={k} className="flex flex-col gap-1 text-xs font-semibold text-brand-muted">{label}
                      <input type="number" step="0.01" className={inputCls} value={appr[k]} onChange={(e) => setAppr((p) => ({ ...p, [k]: e.target.value }))} />
                    </label>
                  ))}
                </div>
                <button type="button" disabled={busy} onClick={() => act({ do: 'approve', ...appr }, 'Mark this application approved with these terms?')} className={`${btn} w-full bg-emerald-600 text-white hover:opacity-90`}>Approve</button>
                <p className="text-xs text-brand-muted">Then send the patient your own quotation and credit agreement to sign.</p>
              </div>
            )}

            {can('decline') && (
              <div className={card + ' space-y-3'}>
                <h2 className="font-semibold text-brand-ink">Decline</h2>
                <select className={inputCls} value={category} onChange={(e) => setCategory(e.target.value)}>
                  <option value="">Choose the reason category</option>
                  {Object.entries(decline_categories).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
                <button type="button" disabled={busy || !category} onClick={() => act({ do: 'decline', category }, 'Mark this application declined?')} className={`${btn} w-full border border-red-200 bg-red-50 text-red-700`}>Decline</button>
                <p className="text-xs text-brand-muted">The practice sees only this category. Give the patient your reasons directly if they ask.</p>
              </div>
            )}

            {can('signed') && (
              <div className={card + ' space-y-3'}>
                <h2 className="font-semibold text-brand-ink">Agreement signed?</h2>
                <button type="button" disabled={busy} onClick={() => act({ do: 'signed' }, 'Confirm the patient has signed your credit agreement?')} className={`${btn} w-full bg-brand-primary text-white hover:opacity-90`}>Mark agreement signed</button>
              </div>
            )}

            {can('ready') && (
              <div className={card + ' space-y-3'}>
                <h2 className="font-semibold text-brand-ink">Ready to book</h2>
                <button type="button" disabled={busy} onClick={() => act({ do: 'ready' }, 'Mark the finance ready, so the patient can book?')} className={`${btn} w-full bg-brand-primary text-white hover:opacity-90`}>Mark ready to book</button>
                <p className="text-xs text-brand-muted">{app.payout_rule}</p>
              </div>
            )}

            {!app.purged_at && (
              <div className={card + ' space-y-3'}>
                <h2 className="font-semibold text-brand-ink">Internal note</h2>
                <textarea className={inputCls} rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder={`Only ${lender.name || 'the lender'} and the practice owner see this.`} />
                <button type="button" disabled={busy} onClick={() => act({ do: 'note', text: note })} className={`${btn} w-full border border-brand-border bg-white text-brand-ink`}>Save note</button>
              </div>
            )}

            {can('withdraw') && is_admin && (
              <button type="button" disabled={busy} onClick={() => act({ do: 'withdraw' }, 'Withdraw this application? The patient will see it as withdrawn.')} className={`${btn} w-full border border-brand-border bg-white text-brand-muted`}>Withdraw application (practice owner)</button>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  )
}
